'use client'

import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { authService } from '@/services/auth'
import type { AuthContextType, AuthUser, AuthSession } from '@/types/auth'
import type { AuthError } from '@supabase/supabase-js'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<AuthUser | null>(null)
    const [session, setSession] = useState<AuthSession | null>(null)
    const [loading, setLoading] = useState(true)
    const router = useRouter()

    useEffect(() => {
        // Get initial session
        const getInitialSession = async () => {
            try {
                const { session, error } = await authService.getSession()
                if (!error && session) {
                    setSession(session)
                    setUser(session.user)
                }
            } catch {
                // Silently handle session retrieval errors
            } finally {
                setLoading(false)
            }
        }

        getInitialSession()

        // Listen for auth changes
        const {
            data: { subscription },
        } = authService.onAuthStateChange((user) => {
            setUser(user)
            if (user) {
                // User signed in - middleware handles redirects from landing/auth pages
                // No client-side redirect needed here to avoid race conditions and flash
            } else {
                // User signed out, redirect to auth if on protected route
                const protectedPaths = ['/stock', '/fund', '/watchlist', '/chat', '/dashboard']
                if (protectedPaths.some((path) => window.location.pathname.startsWith(path))) {
                    router.push('/auth')
                }
            }
            setLoading(false)
        })

        return () => subscription.unsubscribe()
    }, [router])

    // Custom sign up with error handling
    const signUp = useCallback(async (data: Parameters<typeof authService.signUp>[0]) => {
        try {
            const result = await authService.signUp(data)
            if (result.user && !result.error) {
                setUser(result.user)
                setSession(result.session)
            }
            return result
        } catch (error) {
            return {
                user: null,
                session: null,
                error: error as AuthError,
            }
        }
    }, [])

    // Custom sign in with error handling
    const signIn = useCallback(async (data: Parameters<typeof authService.signIn>[0]) => {
        try {
            const result = await authService.signIn(data)
            if (result.user && !result.error) {
                setUser(result.user)
                setSession(result.session)
                // Don't redirect here - let onAuthStateChange handle it to avoid double redirect
            }
            return result
        } catch (error) {
            return {
                user: null,
                session: null,
                error: error as AuthError,
            }
        }
    }, [])

    // Custom sign out with cleanup
    const signOut = useCallback(async () => {
        try {
            const result = await authService.signOut()
            if (!result.error) {
                setUser(null)
                setSession(null)
                router.push('/auth')
            }
            return result
        } catch (error) {
            return { error: error as AuthError }
        }
    }, [router])

    // Memoize the context value to prevent unnecessary re-renders
    const value: AuthContextType = useMemo(
        () => ({
            user,
            session,
            loading,
            signUp,
            signIn,
            signOut,
            resetPassword: authService.resetPassword.bind(authService),
        }),
        [user, session, loading, signUp, signIn, signOut]
    )

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
